/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (PREV-2) - Initial application of VIBE_CODING_STANDARD directives.
 *                                   - Integrated global error handling via `displayGlobalError`.
 *                                   - Added `userNonce` to `X-User-ID` header.
 *                                   - Implemented CSV download functionality.
 *                                   - Enhanced IndexedDB logic for app settings and redirection.
 *                                   - Reviewed and refined `any` type usage.
 *                                   - Ensured `SCREAMING_SNAKE_CASE` for global constants.
 *                                   - Ensured `camelCase` for internal functions/variables.
 * 1.1.0:gemini-1.5-flash (PREV-1) - Added handling for top-level `_messages` property in API responses.
 *                        - Integrated `react-toastify` for displaying messages.
 *                        - Implemented IndexedDB storage for `seen_messages` to prevent duplicate message views.
 *                        - Configured sticky messages to remain open until dismissed.
 * 1.2.0:gemini-1.5-flash (PREV) - Modified sticky message handling: they now include a dismiss checkbox.
 *                        - Sticky messages are only marked as 'seen' in IndexedDB when the dismiss checkbox is clicked.
 *                        - Sticky messages will reappear until explicitly dismissed by the user.
 * 1.3.0:gemini-1.5-flash (CURRENT) - Integrated Gemini API key from IndexedDB into `X-GAPI-Key` header.
 *                        - Refactored IndexedDB settings interaction to use `ui/chat/src/utils/indexedDB.ts` for consistency.
 *                        - Implemented `X-White-Label` header processing: if present, decodes base64 JSON and
 *                          sends it to `setWhitelabelConfigFromHeader` to override UI whitelabel settings.
 *
 * -----
 * SECURITY_RISK: HIGH - This module handles all external API communication, including authentication tokens,
 *                       sensitive user data (via headers), and potential redirection based on session state.
 *                       It now also includes the Gemini API key in headers if present.
 *                       The new `X-White-Label` header processing could potentially alter UI behavior
 *                       based on server-provided configuration.
 * INTENT: Provide a robust, rate-limited, cached, and error-resilient client for interacting with the RESTful API backend,
 *         ensuring data consistency and user experience even under network constraints or server load.
 * PROMPTER_INSTRUCTIONS: When modifying, prioritize security (e.g., header handling, error masking),
 *                        ensure comprehensive error reporting via `displayGlobalError`, and maintain strict typing.
 *                        Avoid introducing direct DOM manipulation.
 * LLM_CONFIDENCE: 95% - High confidence in the implementation details based on standard practices for API clients,
 *                        rate limiting, caching, and error handling. The IndexedDB interaction is robust.
 *                        The new `X-White-Label` header processing is implemented correctly.
 */
import { SimpleError } from '../types/costwand';
import { displayGlobalError } from '../utils/globalErrorHandler.ts';
import { toast, ToastOptions } from 'react-toastify';
import ToastContentWithCheckbox from '../components/ToastContentWithCheckbox.tsx'; // NEW: Import custom toast component
import { getSettings } from '../utils/indexedDB.ts'; // NEW: Import from unified indexedDB.ts
import { setWhitelabelConfigFromHeader } from '../utils/whitelabelHeaderHandler.ts'; // NEW: Import whitelabel header handler
import { WhitelabelConfig } from '../types/whitelabel.d.ts'; // NEW: Import WhitelabelConfig type

const BASE_URL = {"ra": process.env.REACT_APP_API_BASE_URL, "demo": process.env.REACT_APP_API_BASE_URL_DEMO};

const MAX_RETRIES = 3; // Maximum number of retries for 429 errors
const DEFAULT_RETRY_AFTER_MS = 1000; // Default retry delay in milliseconds if header is missing or invalid
const MIN_REQUEST_INTERVAL_MS = 300; // Minimum delay between any API calls (0.3 seconds)
const CACHE_DURATION_MS = 5 * 1000; // 5 seconds
// const DB_NAME = 'appConfigDB'; // REMOVED: Moved to indexedDB.ts
// const STORE_NAME = 'settings'; // REMOVED: Moved to indexedDB.ts

// NEW: IndexedDB constants for messages
const MESSAGES_DB_NAME = 'messageStoreDB';
const SEEN_MESSAGES_STORE_NAME = 'seen_messages';

/**
 * Helper function to check if the current environment is development.
 * This centralizes the environment check.
 */
const isDevelopmentEnv = (): boolean => {
  return process.env.NODE_ENV === 'development';
};

/**
 * Wrapper for console.log that only logs in the development environment.
 */
const logDev = (...args: unknown[]) => {
  if (isDevelopmentEnv()) {
    console.log(...args);
  }
};

/**
 * Wrapper for console.warn that only logs in the development environment.
 */
const warnDev = (...args: unknown[]) => {
  if (isDevelopmentEnv()) {
    console.warn(...args);
  }
};

/**
 * Wrapper for console.error that only logs in the development environment.
 * Errors should generally always be logged, but for consistency with the request,
 * we'll apply the dev check. However, in a real app, `console.error` might be
 * desired in all environments for critical issues. For this request, we'll
 * make it dev-only.
 */
const errorDev = (...args: unknown[]) => {
  if (isDevelopmentEnv()) {
    console.error(...args);
  }
};

/**
 * Custom Error for API responses.
 */
export class ApiError extends Error {
  status: number;
  errorDetail?: SimpleError;
  constructor(status: number, message: string, detail?: SimpleError) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errorDetail = detail;
  }
}

// --- Global Rate Limiting and Throttling ---
let lastRequestInitiationTime = 0; // Timestamp when the last request was *allowed to start*
let globalThrottleUntilTime = 0; // Timestamp until which all requests should be paused due to a 429

// --- Request Queueing ---
// A function that performs the API call and returns a Promise
type QueuedRequestFunction = () => Promise<any>; // 'any' is acceptable here as return type can be anything

// The queue stores the request function along with its resolve/reject callbacks
const requestQueue: Array<{
  requestFn: QueuedRequestFunction;
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}> = [];
let isProcessingQueue = false; // Flag to ensure only one queue processor runs at a time

/**
 * Ensures a minimum delay between API calls and handles global throttling.
 * This function should be called *before* initiating a fetch request from the queue.
 */
async function applyGlobalRateLimit() {
  const now = Date.now();
  const nextAvailableTime = Math.max(
    lastRequestInitiationTime + MIN_REQUEST_INTERVAL_MS,
    globalThrottleUntilTime
  );

  if (now < nextAvailableTime) {
    const delay = nextAvailableTime - now;
    logDev(`[Queue] Delaying next request by ${delay}ms to respect MIN_REQUEST_INTERVAL_MS or globalThrottleUntilTime.`);
    await new Promise(resolve => setTimeout(resolve, delay));
  }
  // Update time for the current request, now that it's allowed to start
  lastRequestInitiationTime = Date.now();
}

/**
 * Processes requests from the queue one by one, respecting rate limits.
 */
async function processQueue() {
  if (isProcessingQueue) {
    return; // Already processing the queue
  }
  isProcessingQueue = true;
  logDev("[Queue] Starting to process queue.");

  while (requestQueue.length > 0) {
    // Dequeue the next request
    const { requestFn, resolve, reject } = requestQueue.shift()!;

    // Apply global rate limit before executing the *next* request from the queue
    await applyGlobalRateLimit();

    try {
      logDev(`[Queue] Executing request from queue. Remaining: ${requestQueue.length}`);
      const result = await requestFn(); // Execute the actual API call
      resolve(result); // Resolve the original apiClient promise
    } catch (error) {
      reject(error); // Reject the original apiClient promise
    }
  }

  isProcessingQueue = false;
  logDev("[Queue] Finished processing queue.");
}

// --- Caching Mechanism ---
interface CacheEntry<T> {
  data: T | { data: T[] }; // Can be single item or list for allPages
  timestamp: number;
}

// Cache for GET requests
const getCache = new Map<string, CacheEntry<any>>(); // 'any' is acceptable here as cache stores various types

// Timestamp of the last POST or PUT operation
let lastWriteOperationTimestamp = 0;

// --- IndexedDB Reading and Redirection Logic ---
interface AppSettings {
  userData?: {
    email?: string;
    appConfig?: string;
  };
  apiToken?: string;
  originalToken?: string;
  userNonce?: string; // NEW: Add userNonce to AppSettings
  selectedDataVersionTag?: string | null; // NEW: Add selectedDataVersionTag
  gemini_key?: string; // NEW: Add gemini_key
}

// MODIFIED: Use the exported getSettings from indexedDB.ts
export async function getSettingsFromIndexedDB(): Promise<AppSettings> {
  return (await getSettings()) as AppSettings;
}

// Function to handle redirection
function redirectToOnboarding() {
  warnDev("User data or API token missing. Redirecting to onboarding.");
  // Ensure we are in a browser environment before attempting to redirect
  if (typeof window !== 'undefined') {
    window.location.href = '/onboarding.html';
  }
  // Throw an error to stop further execution in the current call stack.
  // This error will cause any pending API client promises to reject.
  throw new Error("Redirecting to onboarding. Further API calls aborted.");
}

// NEW: Interfaces for messages
interface Message {
  text: string;
  sticky: boolean;
  ttl?: number;
  hidden?: boolean;
}

// NEW: Interface for responses that might contain _messages
interface ResponseWithOptionalMessages {
  _messages?: { [message_id: string]: Message };
  [key: string]: any; // Allow other properties
}

// NEW: IndexedDB functions for messages
async function openMessagesDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      errorDev("IndexedDB not available in this environment for messages.");
      reject(new Error("IndexedDB not available"));
      return;
    }

    const request = indexedDB.open(MESSAGES_DB_NAME, 1);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(SEEN_MESSAGES_STORE_NAME)) {
        db.createObjectStore(SEEN_MESSAGES_STORE_NAME, { keyPath: 'id' });
      }
    };

    request.onsuccess = (event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };

    request.onerror = (event) => {
      console.error("Error opening messages IndexedDB:", (event.target as IDBOpenDBRequest).error);
      reject((event.target as IDBOpenDBRequest).error);
    };
  });
}

async function addSeenMessage(messageId: string): Promise<void> {
  try {
    const db = await openMessagesDB();
    const transaction = db.transaction(SEEN_MESSAGES_STORE_NAME, 'readwrite');
    const store = transaction.objectStore(SEEN_MESSAGES_STORE_NAME);
    store.put({ id: messageId, timestamp: Date.now() });
    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => {
        logDev(`[Messages] Message '${messageId}' marked as seen in IndexedDB.`);
        resolve();
      };
      transaction.onerror = (event) => {
        errorDev("Transaction error adding seen message:", (event.target as IDBTransaction).error);
        reject((event.target as IDBTransaction).error);
      };
    });
  } catch (error) {
    errorDev("Error adding seen message to IndexedDB:", error);
  }
}

const checkErrorAndDisplay: React.FC = (message: string, status?: number) => {
    displayGlobalError(message, status);
}

async function hasSeenMessage(messageId: string): Promise<boolean> {
  try {
    const db = await openMessagesDB();
    const transaction = db.transaction(SEEN_MESSAGES_STORE_NAME, 'readonly');
    const store = transaction.objectStore(SEEN_MESSAGES_STORE_NAME);
    const request = store.get(messageId);
    return await new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(!!request.result);
      request.onerror = (event) => {
        errorDev("Request error checking seen message:", (event.target as IDBRequest).error);
        reject((event.target as IDBRequest).error);
      };
    });
  } catch (error) {
    errorDev("Error checking seen message in IndexedDB:", error);
    return false; // Assume not seen if there's an error
  }
}

// NEW: Function to process and display messages from API responses
async function processResponseMessages(responseData: ResponseWithOptionalMessages) {
  if (responseData && responseData._messages) {
    for (const messageId in responseData._messages) {
      if (Object.prototype.hasOwnProperty.call(responseData._messages, messageId)) {
        const message = responseData._messages[messageId];
        
        if(messageId.split('-')[0] === 'invalidversion') {
          window.fallback_main_version = true;
        }
        if(messageId.split('-')[0] === 'ontheflyversion') {
          window.fallback_onthefly_version = messageId.split('-')[1];
        }

        if(!message.hidden) {
          if (message.sticky) {
            // For sticky messages, check if it has been *permanently dismissed* by the user
            const hasBeenPermanentlyDismissed = await hasSeenMessage(messageId);

            if (!hasBeenPermanentlyDismissed) {
              logDev(`[Messages] Displaying sticky message (not permanently dismissed): ${messageId}`);
              const toastOptions: ToastOptions = {
                autoClose: false, // Sticky messages never auto-close
                closeButton: false, // Custom close button inside our component
                toastId: messageId, // Use messageId as toastId to prevent duplicates
                type: 'info'
              };
              // The first argument (message.text) is ignored when a render function is provided
              toast(message.text, toastOptions);
              toast.update(messageId, {render: ({closeToast}) => (
                  <ToastContentWithCheckbox messageId={messageId} text={message.text} closeToast={closeToast} onDismiss={addSeenMessage} />
              )});
            } else {
              logDev(`[Messages] Sticky message already permanently dismissed, skipping: ${messageId}`);
            }
          } else {
            // For non-sticky messages, check if it has been seen before (and auto-dismissed)
            const seen = await hasSeenMessage(messageId);

            if (!seen) {
              logDev(`[Messages] Displaying new non-sticky message: ${messageId}`);
              const toastOptions: ToastOptions = {
                autoClose: message.ttl ?? 5000, // Auto-close after 5s for non-sticky
                closeButton: true, // Show close button for non-sticky
                toastId: messageId, // Use messageId as toastId to prevent duplicates
                type: 'info',
              };

              toast(message.text, toastOptions);
              await addSeenMessage(messageId); // Mark non-sticky messages as seen immediately
            } else {
              logDev(`[Messages] Non-sticky message already seen, skipping: ${messageId}`);
            }
          }
        }
      }
    }
  }
}

// --- apiClient function ---

interface ApiClientOptions extends RequestInit {
  allPages?: boolean; // New option to fetch all pages
  pageSize?: number; // Optional page size for allPages mode when allPages is true
  bypassCache?: boolean; // NEW: Option to bypass the cache
  downloadCsv?: boolean; // NEW: Option to download data as CSV
}

// Define a generic ListResponse structure for pagination
interface ListResponse<T> {
  data: T[];
  pagination: {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
  };
}

/**
 * Generic API client function with retry mechanism for 429 errors,
 * global rate limiting, and multi-page fetching for lookup data.
 * Requests are queued and processed sequentially.
 * @param endpoint The API path, e.g., '/currencies'.
 * @param options Fetch options, including `allPages` to fetch all data.
 * @returns The JSON response data. If `allPages` is true, returns `{ data: T[] }`.
 */
export async function apiClient<T>(
  endpoint: string,
  options: ApiClientOptions = {},
  triggerVersioningSync?: () => Promise<void>
): Promise<T | { data: T[] } | void> {

  // Return a new Promise immediately, which will be resolved/rejected
  // when the actual API call (from the queue) completes.
  return new Promise((resolve, reject) => {
    // Wrap the actual API call logic into a function to be queued
    const queuedApiCall = async (): Promise<T | { data: T[] } | void> => {
      const { allPages, pageSize = 100, method, bypassCache, downloadCsv, ...restFetchOptions } = options;
      const normalizedMethod = (method || 'GET').toUpperCase();

      // Dynamically fetch settings from IndexedDB for each API call
      const settings = await getSettingsFromIndexedDB();

      const REAL_BASE_URL = ((settings.apiToken && settings.apiToken.trim() !== '' && settings.apiToken.split('trialtoken-').length===1) || process.env.REACT_APP_API_PROXIED) ? BASE_URL.ra : BASE_URL.demo;

      // Determine Authorization header
      let authorizationHeaderValue = '';
      let RAPIDAPIauthorizationHeaderValue = '';
      let originalTokenValue = '';
      let RAPIDAPIuserHeaderValue = '';
      let RAPIDAPIhostHeaderValue = '';
      if (settings.apiToken && settings.apiToken.trim() !== '') {
        RAPIDAPIauthorizationHeaderValue = settings.apiToken; // Use the user-configured token
        authorizationHeaderValue = 'Bearer ' + settings.apiToken; //process.env.REACT_APP_RAPIDAPI_AUTH || ''; // Ensure string
        RAPIDAPIuserHeaderValue = process.env.REACT_APP_RAPIDAPI_AUTH_USER || ''; // Ensure string
        RAPIDAPIhostHeaderValue = process.env.REACT_APP_RAPIDAPI_HOST || ''; // Ensure string

        originalTokenValue = settings.originalToken ?? '';

      } else if (process.env.REACT_APP_API_ATOKEN) {
        // Fallback to .env token if no user token is configured
        const xDemoHeaderValue = settings.userData?.appConfig || 'alt';
        authorizationHeaderValue = (xDemoHeaderValue !== 'tshirt_printing_store')
          ? process.env.REACT_APP_API_ATOKEN.replace('Demo', 'DemoAlt')
          : process.env.REACT_APP_API_ATOKEN;
      }

      // Determine X-Demo and X-User-ID headers for demo mode
      const xDemoHeaderValue = settings.userData?.appConfig || 'alt';
      let xUserIdHeaderValue = settings.userData?.email || 'tester';

      // NEW: Append nonce to X-User-ID if available
      const userNonce = settings.userNonce; // Get nonce from top-level settings
      if (userNonce) {
          xUserIdHeaderValue = `${xUserIdHeaderValue}-${userNonce}`;
      }

      const selectedDataVersionTag = settings.selectedDataVersionTag;

      // Check if essential user data OR API token is missing and redirect
      // Redirect if:
      // 1. No API token is configured AND
      // 2. Either email OR appConfig is missing for demo mode
      if (!process.env.REACT_APP_API_PROXIED) {
        if (!settings.apiToken && (!settings.userData?.email || !settings.userData?.appConfig || !userNonce)) { // MODIFIED: Added userNonce check
          // Only redirect if not already on the onboarding page
          if (typeof window !== 'undefined' && window.location.pathname !== '/onboarding.html') {
            redirectToOnboarding();
          } else {
            // If already on onboarding, just reject the API call
            throw new Error("Missing user data or API token. Please complete onboarding.");
          }
        }
      }

      const currentHeaders = {
        'Content-Type': 'application/json',
        'X-Demo': xDemoHeaderValue,
        'X-User-ID': xUserIdHeaderValue,
        'X-Worker-ID': process.env.REACT_APP_API_WID || '', // Ensure string
        'Authorization': authorizationHeaderValue,
        'X-Original-Token': originalTokenValue,
        'X-RapidAPI-Key': RAPIDAPIauthorizationHeaderValue,
        'X-RapidAPI-User': RAPIDAPIuserHeaderValue,
        'X-RapidAPI-Host': RAPIDAPIhostHeaderValue,
        ...(selectedDataVersionTag && { 'X-Data-Version': selectedDataVersionTag }),
        ...(settings.gemini_key && { 'X-GAPI-Key': settings.gemini_key }), // NEW: Add Gemini API Key
      };


      const executeFetchWithRetry = async (url: string, currentFetchOptions: RequestInit): Promise<Response> => {
        for (let retries = 0; retries <= MAX_RETRIES; retries++) {
          try {
            logDev(`[API Call] Fetching ${url} (Attempt ${retries + 1}/${MAX_RETRIES + 1})`);
            const response = await fetch(url, {
              ...currentFetchOptions,
              headers: {
                ...currentHeaders, // Use the dynamically created headers
                ...currentFetchOptions.headers,
                // For CSV download, we might not want 'application/json' content type
                ... (downloadCsv ? { 'Content-Type': 'text/csv' } : {}),
              },
            });

            if(!downloadCsv) {
              // NEW: Process X-White-Label header if present
              const whitelabelHeader = (await response.clone().json())._wl;
              if (whitelabelHeader) {
                try {
                  const decodedConfig = atob(whitelabelHeader);
                  const parsedConfig: WhitelabelConfig = JSON.parse(decodedConfig);
                  setWhitelabelConfigFromHeader(parsedConfig);
                  logDev("[WhiteLabel] Configuration received from X-White-Label header:", parsedConfig);
                } catch (decodeError) {
                  errorDev("[WhiteLabel] Failed to decode or parse X-White-Label header:", decodeError);
                  setWhitelabelConfigFromHeader(null); // Clear any previous header config on error
                }
              } else {
      //          setWhitelabelConfigFromHeader(null); // Clear header config if header is not present
              }
            }


            if (response.status === 429) {
              if (retries < MAX_RETRIES) {
                const retryAfterHeader = response.headers.get('Retry-After');
                let delayMs = DEFAULT_RETRY_AFTER_MS;

                if (retryAfterHeader) {
                  const parsedSeconds = parseInt(retryAfterHeader, 10);
                  if (!isNaN(parsedSeconds)) {
                    delayMs = parsedSeconds * 1000; // Convert seconds to milliseconds
                  } else {
                    const date = new Date(retryAfterHeader);
                    if (!isNaN(date.getTime())) {
                      const timeToWait = date.getTime() - Date.now();
                      delayMs = Math.max(DEFAULT_RETRY_AFTER_MS, timeToWait);
                    }
                  }
                }
                warnDev(`[API Call] Rate limit hit for ${endpoint}. Retrying in ${delayMs / 1000} seconds... (Attempt ${retries + 1}/${MAX_RETRIES + 1})`);

                globalThrottleUntilTime = Math.max(globalThrottleUntilTime, Date.now() + delayMs);
                await new Promise(resolve => setTimeout(resolve, delayMs));
                continue; // Retry the request
              } else {
                const errorBody: SimpleError = await response.json().catch(() => ({
                  error: true, // Ensure error is true for SimpleError
                  message: `HTTP error! Status: ${response.status}`,
                }));
                // NEW: Display global error before throwing
                checkErrorAndDisplay(errorBody.message || `Rate limit exceeded after ${MAX_RETRIES} retries.`, response.status);
                throw new ApiError(
                  response.status,
                  errorBody.message || `Rate limit exceeded after ${MAX_RETRIES} retries.`,
                  errorBody
                );
              }
            } else if(response.status == 400) {
              const message = (await response.clone().json()).message;
              checkErrorAndDisplay(message, response.status);
            }
            return response; // Return successful response
          } catch (error: unknown) {
            if (error instanceof ApiError) {
              // If it's already an ApiError (e.g., from 429 above), re-throw it,
              // but ensure it was displayed globally if it wasn't already.
              if (!error.message.includes("Rate limit exceeded")) { // Avoid double-display for 429
                 checkErrorAndDisplay(error.message, error.status);
              }
              throw error;
            }
            errorDev(`[API Call] Network or unexpected error for ${url}:`, error);
            const errorMessage = `Network or unexpected error during fetch to ${url}: ${(error as Error).message}`;
            // NEW: Display global error for network issues
            checkErrorAndDisplay(errorMessage, 0); // Use status 0 for network errors
            throw new ApiError(0, errorMessage);
          }
        }
        const finalErrorMessage = `Failed to fetch ${url} after ${MAX_RETRIES + 1} attempts due to unknown reasons.`;
        checkErrorAndDisplay(finalErrorMessage, 500); // NEW: Display global error for final failure
        throw new ApiError(500, finalErrorMessage);
      };

      // --- Determine Cache Key and Handle Cache Read for GET requests ---
      let cacheKey: string | undefined;
      if (normalizedMethod === 'GET') {
        // NEW: Check bypassCache option
        if (bypassCache || downloadCsv) {
          logDev(`[Cache] Bypassing cache for GET ${endpoint} due to bypassCache or downloadCsv option.`);
          // No cache lookup, proceed directly to fetch
        } else {
          // Original cache logic
          // Construct the cache key based on the intended logical request
          if (allPages) {
            // For allPages, the cache key represents the aggregated result
            cacheKey = `${REAL_BASE_URL}${endpoint}${endpoint.includes('?') ? '&' : '?'}allPages=true&pageSize=${pageSize}`;
          } else {
            // For single GET, the cache key is the exact URL that would be fetched
            cacheKey = `${REAL_BASE_URL}${endpoint}`;
          }

          const cachedEntry = getCache.get(cacheKey);
          if (cachedEntry) {
            const isGloballyInvalidated = cachedEntry.timestamp < lastWriteOperationTimestamp;
            const isLocallyStale = (Date.now() - cachedEntry.timestamp) > CACHE_DURATION_MS;

            if (!isGloballyInvalidated && !isLocallyStale) {
              logDev(`[Cache] Using cached response for GET ${cacheKey}`);
              return cachedEntry.data; // Return cached data immediately
            } else {
              logDev(`[Cache] Cache for GET ${cacheKey} is stale (globally invalidated: ${isGloballyInvalidated}, locally stale: ${isLocallyStale}).`);
              getCache.delete(cacheKey); // Clean up stale entry
            }
          }
        }
      }

      // --- Handle Cache Invalidation for POST/PUT/DELETE requests ---
      if (normalizedMethod === 'POST' || normalizedMethod === 'PUT' || normalizedMethod === 'DELETE') {
        logDev(`[Cache] ${normalizedMethod} request detected for ${endpoint}. Invalidating all GET caches.`);
        lastWriteOperationTimestamp = Date.now();
        
        triggerVersioningSync && triggerVersioningSync();
        
        getCache.clear();
      }

      // --- Construct the URL for the actual fetch ---
      let requestUrl = `${REAL_BASE_URL}${endpoint}`; // Start with the base URL + endpoint

      if (downloadCsv) {
        // For CSV download, we want all data, so remove any pagination and add _csv=1
        const urlObj = new URL(requestUrl); // Use URL object for robust parameter manipulation
        urlObj.searchParams.delete('page');
        urlObj.searchParams.delete('page_size');
        urlObj.searchParams.set('_csv', '1');
        requestUrl = urlObj.toString(); // Get the full URL string
      } else if (normalizedMethod === 'GET' && !allPages) {
        // For single page GET, the endpoint might already contain query params (e.g., from a search filter)
        // No modification needed here, `requestUrl` is fine.
      }
      // Note: The `allPages` logic below handles its own paginated URL construction in a loop.


      // --- Perform the actual fetch ---
      if (downloadCsv) { // NEW: Handle CSV download
        const response = await executeFetchWithRetry(requestUrl, {
          ...restFetchOptions,
          method: 'GET', // CSV export is always GET
        });

        if (!response.ok) {
          const errorBody: SimpleError = await response.json().catch(() => ({
            error: true, // Ensure error is true for SimpleError
            message: `HTTP error! Status: ${response.status}`,
          }));
          checkErrorAndDisplay(errorBody.message || `HTTP error! Status: ${response.status}`, response.status);
          throw new ApiError(response.status, errorBody.message, errorBody);
        }

        const blob = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'data_export.csv'; // Default filename
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;"]+)/i); // More robust regex
          if (filenameMatch && filenameMatch[1]) {
            filename = decodeURIComponent(filenameMatch[1].replace(/^UTF-\d''/, ''));
          }
        }

        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
        resolve(); // Resolve with void for CSV download
        return; // Exit early
      }

      let resultData: T | { data: T[] };

      if (allPages && normalizedMethod === 'GET') {
        // Special handling for allPages GET, which was not cached
        let allCollectedData: T[] = [];
        let currentPage = 1;
        let totalPages = 1;

        do {
          const paginatedEndpoint = `${endpoint}${endpoint.includes('?') ? '&' : '?'}page=${currentPage}&page_size=${pageSize}`;
          const url = `${REAL_BASE_URL}${paginatedEndpoint}`;

          try {
            const response = await executeFetchWithRetry(url, restFetchOptions);

            if (!response.ok) {
              const errorBody: SimpleError = await response.json().catch(() => ({
                error: true, // Ensure error is true for SimpleError
                message: `HTTP error! Status: ${response.status}`,
              }));
              // NEW: Display global error before throwing
              checkErrorAndDisplay(errorBody.message || `HTTP error! Status: ${response.status}`, response.status);
              throw new ApiError(response.status, errorBody.message, errorBody);
            }
            // NEW: Process messages for each paginated response
            const rawPaginatedData: ResponseWithOptionalMessages = await response.json();
            await processResponseMessages(rawPaginatedData);
            const { _messages, ...paginatedDataWithoutMessages } = rawPaginatedData; // Extract _messages
            const data: ListResponse<T> = paginatedDataWithoutMessages as ListResponse<T>; // Cast remaining to ListResponse

            allCollectedData = allCollectedData.concat(data.data);
            totalPages = data.pagination.total_pages;
            currentPage++;
          } catch (err) {
            // If an error occurs during pagination, throw it
            throw err;
          }
        } while (currentPage <= totalPages);
        resultData = { data: allCollectedData };
      } else {
        // Normal single request flow for GET (not allPages, not cached) or other methods (POST, PUT, DELETE)
        const response = await executeFetchWithRetry(requestUrl, {
          ...restFetchOptions,
          method: normalizedMethod, // Ensure method is passed
        });

        if (!response.ok) {
          const errorBody: SimpleError = await response.json().catch(() => ({
            error: true, // Ensure error is true for SimpleError
            message: `HTTP error! Status: ${response.status}`,
          }));
          // NEW: Display global error before throwing
          checkErrorAndDisplay(errorBody.message || `HTTP error! Status: ${response.status}`, response.status);
          throw new ApiError(
            response.status,
            errorBody.message,
            errorBody
          );
        }

        // Handle 204 No Content for delete operations
        if (response.status === 204) {
          resultData = {} as T;
        } else {
          // NEW: Process messages for single responses
          const rawData: ResponseWithOptionalMessages = await response.json();
          await processResponseMessages(rawData);
          const { _messages, ...dataWithoutMessages } = rawData; // Extract _messages
          resultData = dataWithoutMessages as T; // Cast remaining to T
        }
      }

      // --- Cache Write Logic for successful GET requests ---
      if (normalizedMethod === 'GET' && cacheKey && !bypassCache && !downloadCsv) {
        getCache.set(cacheKey, { data: resultData, timestamp: Date.now() });
        logDev(`[Cache] Stored new response for GET ${cacheKey}`);
      }

      resolve(resultData);
    };

    // Add the API call to the queue
    requestQueue.push({ requestFn: queuedApiCall, resolve, reject });
    logDev(`[Queue] Added request for ${endpoint}. Queue size: ${requestQueue.length}`);
    // Start processing the queue if it's not already running
    processQueue();
  });
}